// Canonical status badge colors and labels for web-admin (docs/UI-GUIDELINES.md section 3.3).
// CoreUI color names: error -> "danger", grey -> "secondary".
// Keyed by the backend enum name (the API's `statusDescription` / `stateDescription` strings).
// Numeric enum values are accepted too (index into `order`).
//
// Usage:
//   import { badgeColor, badgeLabel } from "@/utils/statusBadges";
//   <CBadge :color="badgeColor('invoice', item.statusDescription)">
//     {{ badgeLabel('invoice', item.statusDescription) }}
//   </CBadge>

const DOCUMENT = {
  order: ["Draft", "Approve"],
  map: {
    Draft: ["secondary", "Draft"],
    Approve: ["primary", "Approved"],
    Approved: ["primary", "Approved"],
  },
};

const OFFER = {
  // QuotationStatus / PurchaseOrderStatus / IncomeReceiptStatus
  order: ["Draft", "Sent", "Accepted", "Rejected", "Cancelled"],
  map: {
    Draft: ["secondary", "Draft"],
    Sent: ["info", "Sent"],
    Accepted: ["success", "Accepted"],
    Rejected: ["danger", "Rejected"],
    Cancelled: ["secondary", "Cancelled"],
  },
};

const TYPES = {
  invoice: DOCUMENT,
  bill: DOCUMENT,
  // Invoice/bill payment state, derived on the client: "Unpaid" | "Partial" | "Paid" | "Overdue"
  documentPayment: {
    order: ["Unpaid", "Partial", "Paid", "Overdue"],
    map: {
      Unpaid: ["warning", "Unpaid"],
      Partial: ["warning", "Partly paid"],
      Paid: ["success", "Paid"],
      Overdue: ["danger", "Overdue"],
    },
  },
  quotation: OFFER,
  purchaseQuotation: OFFER,
  purchaseOrder: OFFER,
  incomeReceipt: OFFER,
  paymentVoucher: {
    order: ["Draft", "PendingApproval", "Approved", "Paid", "Reconciled", "Rejected", "Voided"],
    map: {
      Draft: ["secondary", "Draft"],
      PendingApproval: ["warning", "Waiting for approval"],
      Approved: ["primary", "Approved"],
      Paid: ["success", "Paid"],
      Reconciled: ["success", "Reconciled"],
      Rejected: ["danger", "Rejected"],
      Voided: ["secondary", "Voided"],
    },
  },
  expense: {
    order: ["Draft", "Approve", "Paid", "Cancelled"],
    map: {
      Draft: ["secondary", "Draft"],
      Approve: ["primary", "Approved"],
      Approved: ["primary", "Approved"],
      Paid: ["success", "Paid"],
      Cancelled: ["secondary", "Cancelled"],
    },
  },
  active: {
    order: [],
    map: {
      true: ["success", "Active"],
      false: ["secondary", "Inactive"],
    },
  },
};

function entry(type, status) {
  const def = TYPES[type];
  if (!def) return null;
  let key = status;
  if (typeof status === "number" && def.order[status] !== undefined) key = def.order[status];
  if (type === "active") key = String(!!status);
  return def.map[key] || null;
}

/** Turn an unknown enum name into readable sentence-case text ("PendingApproval" -> "Pending approval"). */
function humanize(status) {
  if (status === null || status === undefined || status === "") return "";
  const words = String(status)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** CoreUI color name for a status ("primary", "success", "warning", "danger", "info", "secondary"). */
export function badgeColor(type, status) {
  const e = entry(type, status);
  return e ? e[0] : "secondary";
}

/** Human, sentence-case label for a status. Never returns the raw enum name. */
export function badgeLabel(type, status) {
  const e = entry(type, status);
  return e ? e[1] : humanize(status);
}

/**
 * Resolved color value for charts (canvas can't use CSS classes). Reads the CoreUI CSS
 * variable for the badge color, so charts match the badges without hard-coded hex values.
 */
export function badgeChartColor(type, status) {
  const name = badgeColor(type, status);
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();
    if (v) return v;
  } catch (e) {
    /* not in a browser */
  }
  return "grey";
}

/**
 * Payment state of an approved invoice/bill: "Paid" | "Partial" | "Overdue" | "Unpaid".
 * `paid` and `total` are numbers; `dueDate` is optional ("YYYY-MM-DD" or ISO).
 */
export function paymentState({ isPaid, paid, total, dueDate, today }) {
  const outstanding = Math.max(0, (Number(total) || 0) - (Number(paid) || 0));
  if (isPaid || (Number(total) > 0 && outstanding <= 0.01)) return "Paid";
  const due = dueDate ? String(dueDate).slice(0, 10) : null;
  if (due && today && due < today) return "Overdue";
  if ((Number(paid) || 0) > 0.01) return "Partial";
  return "Unpaid";
}

export default { badgeColor, badgeLabel, badgeChartColor, paymentState };
